// Shared presentational parts for the CMS fire-and-poll modals (eligibility + align).
// MbiBlock + clipboard stay eligibility-local — no MBI-copy surface on align.

import { useState } from 'react';

/* Animated progress panel — spinner + timed step list + footnote. */
export function LoadingPanel({ title, stepIndex, steps, footnote }) {
  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <Spinner />
        <p className="header4" style={{ margin: 0, color: 'var(--wi-text-primary)' }}>{title}</p>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map((label, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <li key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <StepDot state={done ? 'done' : active ? 'active' : 'pending'} />
              <span
                className="body2"
                style={{
                  color: done || active ? 'var(--wi-text-primary)' : 'var(--wi-text-placeholder)',
                  fontWeight: active ? 500 : 400,
                }}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
      {footnote && (
        <p className="detail1" style={{ margin: '20px 0 0', color: 'var(--wi-text-placeholder)' }}>
          {footnote}
        </p>
      )}
    </div>
  );
}

export function StepDot({ state }) {
  if (state === 'done') {
    return (
      <span
        aria-hidden
        style={{
          width: 16, height: 16, borderRadius: '50%',
          background: 'var(--wi-status-good)',
          color: 'var(--wi-on-action-primary)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span
        aria-hidden
        style={{
          width: 16, height: 16, borderRadius: '50%',
          background: 'var(--wi-status-info-10)',
          border: '2px solid var(--wi-action-primary)',
          flexShrink: 0,
          animation: 'wi-pulse 1.4s ease-in-out infinite',
        }}
      />
    );
  }
  return (
    <span
      aria-hidden
      style={{
        width: 16, height: 16, borderRadius: '50%',
        background: 'transparent',
        border: '2px solid var(--wi-divider)',
        flexShrink: 0,
      }}
    />
  );
}

export function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        width: 22, height: 22, flexShrink: 0,
        border: '2.5px solid var(--wi-action-secondary)',
        borderTopColor: 'var(--wi-action-primary)',
        borderRadius: '50%',
        animation: 'wi-spin 0.85s linear infinite',
      }}
    />
  );
}

/* Verdict banner — top-of-modal, color-coded by tone. */
/* Long banner copy collapses to 2 lines behind "See more". Some explainers are workflow-authored
   (WF37 builds its blocked-submission reason at runtime), so length is not controllable at the
   call site — the bubble has to cope with whatever arrives. */
const LONG_TEXT_CHARS = 200;

/** @param {{ text?: any, color?: string, clampLines?: number }} props */
export function ExpandableText({ text, color, clampLines = 2 }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = String(text).length > LONG_TEXT_CHARS;
  const clamped = long && !open;
  return (
    <div>
      <p
        className="body2"
        style={{
          margin: 0,
          color,
          lineHeight: 1.45,
          ...(clamped ? {
            display: '-webkit-box',
            WebkitLineClamp: clampLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } : {}),
        }}
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{
            marginTop: 4,
            padding: 0,
            border: 'none',
            background: 'none',
            color: 'inherit',
            font: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {open ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
}

/**
 * Props are optional in practice (the component guards each one), but a .jsx destructure without
 * defaults makes tsc infer them all as required — annotate rather than adding `= null` defaults,
 * which would widen the type to `null | undefined` for callers passing a real value.
 * @param {{ tone?: string, headline?: any, resultCode?: any, fhirDisplay?: any, explainer?: any }} props
 */
export function VerdictBanner({ tone, headline, resultCode, fhirDisplay, explainer }) {
  const palette = {
    good:     { bg: 'var(--wi-background-good)',     fg: 'var(--wi-text-primary-good)',     accent: 'var(--wi-status-good)' },
    moderate: { bg: 'var(--wi-background-moderate)', fg: 'var(--wi-text-primary-moderate)', accent: 'var(--wi-status-moderate)' },
    bad:      { bg: 'var(--wi-background-bad)',      fg: 'var(--wi-text-primary-bad)',      accent: 'var(--wi-status-bad)' },
    info:     { bg: 'var(--wi-background-info)',     fg: 'var(--wi-text-primary-info)',     accent: 'var(--wi-status-info)' },
  }[tone] || {};
  return (
    <div
      style={{
        background: palette.bg,
        borderRadius: 10,
        padding: '14px 16px',
        marginBottom: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: explainer || fhirDisplay ? 8 : 0 }}>
        <VerdictGlyph tone={tone} color={palette.accent} />
        {/* The headline takes the space first; minWidth:0 lets it wrap instead of shoving the
            code chip off. Before this the chip claimed its full monospace width and the headline
            broke mid-phrase beside it. */}
        <h3 className="header4" style={{ margin: 0, color: palette.fg, flex: 1, minWidth: 0 }}>{headline}</h3>
        {resultCode && (
          <span
            style={{
              flexShrink: 0,
              marginTop: 2,
              padding: '2px 8px',
              borderRadius: 999,
              background: 'var(--wi-background-secondary)',
              color: palette.fg,
              opacity: 0.75,
              // A machine code for support, not something to read first — one step below the body.
              fontSize: 11,
              lineHeight: '16px',
              whiteSpace: 'nowrap',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            }}
          >
            {resultCode}
          </span>
        )}
      </div>
      {fhirDisplay && fhirDisplay !== headline && (
        <p className="body2" style={{ margin: '0 0 4px', color: palette.fg, fontWeight: 500 }}>
          {fhirDisplay}
        </p>
      )}
      {explainer && <ExpandableText text={explainer} color={palette.fg} />}
    </div>
  );
}

export function VerdictGlyph({ tone, color }) {
  if (tone === 'good') {
    return (
      <span
        aria-hidden
        style={{
          width: 26, height: 26, borderRadius: '50%',
          background: color, color: 'var(--wi-on-action-primary)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }
  if (tone === 'moderate') {
    return (
      <span
        aria-hidden
        style={{
          width: 26, height: 26, borderRadius: '50%',
          background: color, color: 'var(--wi-text-primary)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="8" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </span>
    );
  }
  // 'info' is a neutral state (the pre-submit review) — without its own glyph it falls through to
  // the error cross below, so "Ready to submit" renders wearing a red ✕.
  if (tone === 'info') {
    return (
      <span
        aria-hidden
        style={{
          width: 26, height: 26, borderRadius: '50%',
          background: color, color: 'var(--wi-on-action-primary)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="11" x2="12" y2="16" />
          <line x1="12" y1="7.5" x2="12.01" y2="7.5" />
        </svg>
      </span>
    );
  }
  // bad / default
  return (
    <span
      aria-hidden
      style={{
        width: 26, height: 26, borderRadius: '50%',
        background: color, color: 'var(--wi-on-action-primary)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </span>
  );
}

export function Section({ label, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <p className="eyebrow" style={{ margin: '0 0 8px', color: 'var(--wi-text-secondary)' }}>{label}</p>
      {children}
    </div>
  );
}

export function Callout({ tone, children }) {
  const bg = {
    good: 'var(--wi-background-good)',
    moderate: 'var(--wi-background-moderate)',
    bad: 'var(--wi-background-bad)',
    info: 'var(--wi-background-info)',
  }[tone] || 'var(--wi-background-primary)';
  return (
    <div style={{ background: bg, borderRadius: 8, padding: 12 }}>
      {children}
    </div>
  );
}

/* Inject keyframes once (idempotent — guards against duplicate insertion in tests/HMR). */
if (typeof document !== 'undefined' && !document.getElementById('wi-cms-modal-anims')) {
  const style = document.createElement('style');
  style.id = 'wi-cms-modal-anims';
  style.textContent = `
    @keyframes wi-spin { to { transform: rotate(360deg); } }
    @keyframes wi-pulse {
      0%, 100% { box-shadow: 0 0 0 0 var(--wi-status-info-10); }
      50%      { box-shadow: 0 0 0 6px var(--wi-status-info-10); }
    }
  `;
  document.head.appendChild(style);
}
